import { useContext, useEffect, useState } from "react";
import { AlertModalBinIcon } from "../../../assets";
import { AppLayout } from "../../../containers/layout/app";
import { CurrencyExchangeRateWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { BaseAlertModal } from "../../../components/modal/alert";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { BaseButton } from "../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { CurrencyExchangeRateTable } from "../../../containers/tables/currencyexchangeratetable";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { deleteCurrencyExchangeRateService } from "../../../utils/api/configuration/currencyexchangerate/deleteCurrencyExchangeRate";
import { retrieveAllCurrencyExchangeRatesService } from "../../../utils/api/configuration/currencyexchangerate/retrieveAllCurrencyExchangeRates";

export const CurrencyExchangeRate = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);

	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [currencyExchangeRateIdToDelete, setCurrencyExchangeRateIdToDelete] =
		useState<string | null>(null);
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Configuration");
	}, [setActiveNavItem]);

	const { data: currencyExchangeRates, refetch: refetchCurrencyExchangeRates } =
		useQuery({
			queryKey: ["currencyexchangerates", TOKEN, paginationIndex],
			queryFn: async () => {
				setIsFetching(true);
				const response = await retrieveAllCurrencyExchangeRatesService(
					TOKEN,
					paginationIndex,
				);
				if (!Array.isArray(response) && response?.meta) {
					setPaginationIndex((prev) => ({
						...prev,
						totalPages: String(response?.meta?.totalPages ?? "1"),
					}));
				}
				setIsFetching(false);
				return response.data;
			},
			enabled: !!TOKEN,
		});

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		return setIsAlertModalOpen(false);
	};

	const handleAddCurrencyExchangeRateButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate("/configuration/addcurrencyexchangerate");
	};

	const handleActionClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string | Record<any, any>,
		action?: string,
	) => {
		e.preventDefault();
		switch (action) {
			case "edit":
				return navigate(`/configuration/currencyexchangerate/${id}`);
			case "delete":
				if (typeof id !== "string") {
					const { currencyExchangeRateId, institutionId } = id;
					return handleDeleteCurrencyExchangeRate(
						e,
						institutionId,
						currencyExchangeRateId,
					);
				}
				break;
			default:
				console.warn(`Unhandled action: ${action}`);
		}
	};

	const handleDeleteCurrencyExchangeRate = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		institutionId: string,
		currencyExchangeRateId: string,
	) => {
		e.preventDefault();
		setError(null);
		setCurrencyExchangeRateIdToDelete(currencyExchangeRateId);
		try {
			const response = await deleteCurrencyExchangeRateService(
				TOKEN,
				institutionId,
				currencyExchangeRateId,
			);
			if (response.status === "success") {
				setCurrencyExchangeRateIdToDelete(null);
				setIsAlertModalOpen(true);
				refetchCurrencyExchangeRates();
			} else {
				setCurrencyExchangeRateIdToDelete(null);
				setError("Currency exchange rate deletion failed. Please try again.");
			}
		} catch (error: any) {
			setCurrencyExchangeRateIdToDelete(null);
			setError(`Currency exchange rate deletion failed. ${error.message}`);
			console.error("Currency exchange rate deletion failed:", error);
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	return (
		<AppLayout
			pageId={"Currency-Exchange-Rate"}
			pageTitle={"Currency Exchange Rate"}
		>
			<CurrencyExchangeRateWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalBinIcon />}
					title="Currency Exchange Rate Successfully Deleted"
					callToAction="Close"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<BaseButton
						variant="contained"
						bgcolor="var(--primary-color)"
						padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
						startIcon={<AddIcon />}
						sx={{
							width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							float: { miniTablet: "right" },
						}}
						onClick={handleAddCurrencyExchangeRateButtonClick}
					>
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Add
						</Typography>
					</BaseButton>
				</Box>
				<Box component={"div"} className="currency-exchange-rate-table-box">
					<CurrencyExchangeRateTable
						rows={currencyExchangeRates as any[]}
						selectedCurrencyExchangeRateId={currencyExchangeRateIdToDelete}
						handleItemClick={handleActionClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
				{error && (
					<Typography
						fontFamily={"Inter"}
						fontWeight={"600"}
						fontSize={14}
						lineHeight={"normal"}
						color={"var(--text-salmon-pink-color)"}
						whiteSpace={"normal"}
					>
						{error}
					</Typography>
				)}
			</CurrencyExchangeRateWrapper>
		</AppLayout>
	);
};
